/// <amd-module name="Repos/Views/Settings/Options/Components/OptionToggle" />
import React = require("react");
import { ComplexToggle } from "Repos/Views/Settings/Common/Components/ComplexToggle";
import { IOption } from "Repos/Views/Settings/Options/Contracts/Contracts";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { FormItem } from "VSSUI/FormItem";
import { Observer } from "VSSUI/Observer";
import { TextField, TextFieldWidth } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";

interface IOptionToggleProps {
    option: IOption;
    onToggle: (option: IOption, newText: string | undefined) => Promise<string | undefined>;
    disabled: boolean;
}

export class OptionToggle extends VssComponent<IOptionToggleProps> {
    private disabled: IObservableValue<boolean>;
    private isToggled: IObservableValue<boolean>;
    private text: IObservableValue<string | undefined>;
    private textValidationError: IObservableValue<string | undefined>;
    private lastSavedTextValue: string | undefined;

    public constructor(props: IOptionToggleProps, context: IVssComponentContext) {
        super(props, context);

        const { option, disabled } = props;

        // Do not reuse this as a whole observable - copy the value locally as we mutate it while toggling
        this.disabled = new ObservableValue<boolean>(disabled);
        this.isToggled = new ObservableValue<boolean>(option.value);
        this.text = new ObservableValue(option.value ? option.textValue : option.defaultTextValue);
        this.textValidationError = new ObservableValue("");
        this.lastSavedTextValue = this.isToggled.value ? this.text.value : undefined;
    }

    componentDidUpdate() {
        // Refresh the disabled state of this toggle if the Disable Repo toggle affected it
        this.disabled.value = this.props.disabled;
    }

    public render = (): JSX.Element => {
        const { option } = this.props;
        return (
            <ComplexToggle
                description={option.description}
                disabled={this.disabled}
                id={option.key}
                isToggled={this.isToggled}
                learnMoreLink={option.link}
                onToggle={(value: boolean) => {
                    this.isToggled.value = value;
                    if (!value) {
                        this.text.value = option.defaultTextValue;
                    }
                    this.updateOptions();
                }}
                title={option.title}
            >
                {option.defaultTextValue && (
                    <Observer validationError={this.textValidationError}>
                        {({ validationError }: { validationError: string | undefined }) => (
                            <FormItem error={Boolean(validationError)} message={validationError}>
                                <TextField
                                    ariaLabel={option.title}
                                    className="margin-top-8"
                                    disabled={!this.isToggled.value}
                                    onChange={(event, value) => {
                                        this.text.value = value;
                                    }}
                                    onKeyDown={event => {
                                        if (event.keyCode === KeyCode.enter) {
                                            this.updateOptions();
                                        }
                                    }}
                                    onBlur={this.updateOptions}
                                    placeholder={option.defaultTextValue}
                                    value={this.text}
                                    width={TextFieldWidth.standard}
                                />
                            </FormItem>
                        )}
                    </Observer>
                )}
            </ComplexToggle>
        );
    };

    private updateOptions = async () => {
        if (this.disabled.value) {
            return;
        }

        const newText = this.isToggled.value ? this.text.value : undefined;
        if (this.props.option.defaultTextValue && newText === this.lastSavedTextValue) {
            // If it's a toggle with text field, don't save if the text didn't change
            return;
        }

        this.disabled.value = true;
        this.lastSavedTextValue = newText;
        const validationError = await this.props.onToggle(this.props.option, newText);

        // If we're updating it's because it wasn't disabled
        this.disabled.value = false;
        this.textValidationError.value = validationError;
        // Update props.option that was mutated internally.
        this.forceUpdate();
    };
}
